import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  BarChart, Bar, Legend
} from "recharts";

const API = "https://back.dataregion.com.co/api/analytics";

const TABS = [
  { key: "nuevas", label: "Entradas" },
  { key: "cierres", label: "Salidas" },
  { key: "stock", label: "Stock" },
  { key: "detalle", label: "Detalle sector" },
];

function pivotMatrix(rows, rowKey, colKey, valKey) {
  // rows: [{sector, year, value}]
  const cols = Array.from(new Set(rows.map(r => r[colKey]))).sort((a,b)=>a-b);
  const groups = new Map();

  for (const r of rows) {
    const rk = r[rowKey] ?? "—";
    if (!groups.has(rk)) groups.set(rk, { [rowKey]: rk });
    groups.get(rk)[r[colKey]] = (groups.get(rk)[r[colKey]] || 0) + (r[valKey] || 0);
  }

  return { cols, data: Array.from(groups.values()) };
}

export default function PowerbiLikeDashboard({ token }) {
  const [tab, setTab] = useState("nuevas"); // nuevas|cierres|stock|detalle
  const [periodo, setPeriodo] = useState("2019-2024");
  const [municipio, setMunicipio] = useState("Barrancabermeja");
  const [sector, setSector] = useState("Todas");

  const [filters, setFilters] = useState({ municipios: [], sectores: [] });
  const [seriesYear, setSeriesYear] = useState([]);
  const [matrix, setMatrix] = useState([]); // rows sector-year
  const [kpis, setKpis] = useState({ total: 0, rows: 0 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const dataset = tab === "detalle" ? "stock" : tab; // detalle usa stock por defecto (ajustable)

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/filters?dataset=${dataset}`, {
      headers: { Authorization: "Bearer " + token },
    })
    .then(r => setFilters(r.data))
    .catch(() => {});
  }, [token, dataset]);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    setErr("");

    const params = { dataset, periodo, municipio, sector };

    Promise.all([
      axios.get(`${API}/kpis`, { params, headers: { Authorization: "Bearer " + token } }),
      axios.get(`${API}/series/year`, { params, headers: { Authorization: "Bearer " + token } }),
      axios.get(`${API}/matrix/sector-year`, {
        params: { dataset, periodo, municipio }, // matriz como en powerbi: sector x año
        headers: { Authorization: "Bearer " + token }
      }),
    ])
      .then(([k, s, m]) => {
        setKpis(k.data);
        setSeriesYear(s.data);
        setMatrix(m.data);
      })
      .catch((e) => {
        setErr(e.response?.data?.message || e.message);
      })
      .finally(() => setLoading(false));
  }, [token, dataset, periodo, municipio, sector]);

  const matrixPivot = useMemo(() => pivotMatrix(matrix, "sector", "year", "value"), [matrix]);

  return (
    <div className="dr-search">
      {/* header estilo powerbi */}
      <div className="dr-search-head">
        <h2 className="dr-search-title">Indicadores empresariales</h2>

        <div className="dr-search-bar">
          <select className="dr-search-select" value={municipio} onChange={(e)=>setMunicipio(e.target.value)}>
            <option value="Todas">Todas</option>
            {(filters.municipios || []).map(m => <option key={m} value={m}>{m}</option>)}
          </select>

          <select className="dr-search-select" value={sector} onChange={(e)=>setSector(e.target.value)}>
            <option value="Todas">Todas</option>
            {(filters.sectores || []).map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          <input
            className="dr-search-input"
            value={periodo}
            onChange={(e)=>setPeriodo(e.target.value)}
            placeholder="Periodo (ej: 2019-2024)"
          />
        </div>

        {err && <p className="small" style={{color:"#b52b27"}}>❌ {err}</p>}
        {loading && <p className="small">Cargando…</p>}
      </div>

      {/* tabs */}
      <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
        {TABS.map(t => (
          <button
            key={t.key}
            type="button"
            className="dr-chip"
            style={{
              background: tab === t.key ? "#0e5a77" : "#fff",
              color: tab === t.key ? "#fff" : "#0e5a77",
              borderColor: "#0e5a77",
            }}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* contenido */}
      <div className="dr-search-grid">
        <div>
          {/* linea total por año */}
          <div className="dash-card" style={{ marginBottom: 14 }}>
            <h3 style={{ marginTop: 0 }}>
              {tab === "nuevas" ? "Total nuevas empresas por Año"
                : tab === "cierres" ? "Total empresas que salen por Año"
                : tab === "stock" ? "Total empresas por Año"
                : "Total empresas por Año"}
            </h3>

            <div style={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={seriesYear}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="value" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* matriz sector x año (como tu tabla de la derecha en powerbi) */}
          <div className="dash-card">
            <h3 style={{ marginTop: 0 }}>Sector</h3>
            <div className="table-wrapper">
              <table className="inst-table">
                <thead>
                  <tr>
                    <th>Sector</th>
                    {matrixPivot.cols.map(y => <th key={y}>{y}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {matrixPivot.data.slice(0, 12).map((row) => (
                    <tr key={row.sector}>
                      <td>{row.sector}</td>
                      {matrixPivot.cols.map(y => (
                        <td key={y}>{Number(row[y] || 0).toLocaleString("es-CO")}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="small">Mostrando los primeros 12 sectores (puedes paginar si quieres).</p>
            </div>
          </div>
        </div>

        {/* sidebar tipo powerbi */}
        <aside className="dr-sidebar">
          <div className="dr-box">
            <div className="dr-box-head">Resumen</div>
            <div style={{ padding: 12 }}>
              <p className="small" style={{ margin: 0 }}>
                Dataset: <strong>{dataset}</strong>
              </p>
              <p className="small" style={{ margin: "8px 0 0" }}>
                Total: <strong>{Number(kpis.total || 0).toLocaleString("es-CO")}</strong>
              </p>
              <p className="small" style={{ margin: "8px 0 0" }}>
                Filas: <strong>{Number(kpis.rows || 0).toLocaleString("es-CO")}</strong>
              </p>
            </div>
          </div>

          <div className="dr-box">
            <div className="dr-box-head">Notas</div>
            <button className="dr-box-item" type="button">
              Los gráficos se generan desde MySQL (PowerbiFacts), no desde Power BI.
            </button>
            <button className="dr-box-item" type="button">
              Los filtros aplican a Municipio / Sector / Periodo.
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
